import { metrics } from '@opentelemetry/api'
import * as Sentry from '@sentry/browser'
import throttle from 'lodash/throttle'
import { shallow } from 'zustand/shallow'

import client from '~/clients/api-client'
import { getFlag, getNumberFlag } from '~/hooks/useFlag'
import { selectHasWriteAccess } from '~/hooks/useHasWriteAccess'
import { useStore } from '~/store/store'
import { ScheduledSurgery, UnScheduledSurgery } from '~/storm'
import { getBookingIdOrId, getOrgUnitGuids } from '~/utils/dips'
import { INSTRUMENTATION_NAME } from '~/utils/observability'

import { createPlanningNoteCapture, type PlanningNoteObservation } from './planningNoteCapture'

const HISTORY_SYNC_INTERVAL_MS = {
    // 1 min: live-suggestions-only mode, so we sync history only
    default: 60 * 1000,
    // 10 min: each sync kicks off batch analysis; keep the syncs far apart to let each run finish, until race conditions are fixed
    withBatchSuggestions: 10 * 60 * 1000,
}

export type PlanningNote = {
    booking_id: string
    dips_version: number
    folder_id: string
    planning_note: string
}

type DipsSurgery = ScheduledSurgery | UnScheduledSurgery

function planningNoteFromDipsSurgery(surgery: DipsSurgery): PlanningNote | undefined {
    // Silently skipping invalid data is not ideal, but this legacy path is being removed; raw capture preserves it for quarantine.
    try {
        const booking_id = getBookingIdOrId(surgery)
        if (typeof booking_id !== 'string') return

        const dips_version = surgery.version
        const folder_id = surgery.surgeryOrderDetails?.folderId
        const planning_note = surgery.planningNote

        if (typeof dips_version !== 'number') return
        if (typeof folder_id !== 'string') return
        if (typeof planning_note !== 'string') return

        return { booking_id, dips_version, folder_id, planning_note }
    } catch {
        console.error('Error converting surgery to planning note')
    }
}

async function sendPlanningNotes(notes: PlanningNote[]): Promise<void> {
    if (notes.length === 0) return

    const shouldTriggerSuggestions = getFlag('planning_note__calculate_suggestions')

    try {
        const { error } = await client.syncPlanningNotes({ notes, calculate_suggestions: false })

        if (error) {
            Sentry.captureException(new Error(`Planning note sync failed: ${error}`), {
                extra: { error, count: notes.length },
            })

            console.error('[dipsSubscriptions] Planning note sync failed:', error)
            return
        }

        if (shouldTriggerSuggestions) {
            const triggerResult = (await client.triggerSuggestionsAnalysis()) as { error?: string | null }
            if (triggerResult.error !== null && triggerResult.error !== undefined) {
                console.error('[dipsSubscriptions] Planning note trigger failed:', triggerResult.error)
            }
        }
    } catch (error) {
        Sentry.captureException(new Error(`Planning note sync error: ${String(error)}`), {
            extra: { count: notes.length },
        })

        console.error('[dipsSubscriptions] Planning note sync error:', error)
    }
}

export function convertToPlanningNotes(scheduledArray: ScheduledSurgery[], unscheduledArray: UnScheduledSurgery[]): PlanningNote[] {
    const all = [...scheduledArray, ...unscheduledArray]
    const notesMap = new Map<string, PlanningNote>()
    let skipped = 0

    for (const surgery of all) {
        const next = planningNoteFromDipsSurgery(surgery)
        if (!next) {
            skipped++
            continue
        }

        const current = notesMap.get(next.folder_id)
        // Legacy-only: dips_version can reset on a new booking, so taking the maximum
        // within a folder can retain an older booking's note. Raw capture does not apply this selection.
        if (!current || current.dips_version <= next.dips_version) {
            notesMap.set(next.folder_id, next)
        }
    }

    if (skipped > 0) console.warn('[dipsSubscriptions] Incomplete observations skipped by legacy history sync', { count: skipped })
    return Array.from(notesMap.values())
}

function convertToRawDipsPlanningNoteObservations(scheduledArray: ScheduledSurgery[], unscheduledArray: UnScheduledSurgery[]): PlanningNoteObservation[] {
    return [...scheduledArray, ...unscheduledArray].map(surgery => ({
        booking_guid: getBookingIdOrId(surgery),
        folder_guid: surgery.surgeryOrderDetails?.folderId ?? null,
        planning_note: surgery.planningNote ?? null,
        dips_version: surgery.version ?? null,
        postponement_count: surgery.postponementCount ?? null,
        // Also includes patient_guid.
        ...getOrgUnitGuids(surgery),
    }))
}

export function initDipsSubscriptions() {
    const useAutoSync = getFlag('planning_note__history_sync')
    const batchSuggestionsEnabled = getFlag('planning_note__calculate_suggestions')
    const historySyncIntervalMs = batchSuggestionsEnabled ? HISTORY_SYNC_INTERVAL_MS.withBatchSuggestions : HISTORY_SYNC_INTERVAL_MS.default

    let pendingNotes: PlanningNote[] = []
    const capture = createPlanningNoteCapture({
        meter: metrics.getMeter(INSTRUMENTATION_NAME),
        send: (observations, signal) => client.captureRawDipsPlanningNoteObservations({ observations }, signal),
        getOptions: () => ({
            enabled: getFlag('planning_note__raw_capture'),
            batchSize: getNumberFlag('planning_note__raw_capture_batch_size'),
        }),
        reportFailure: (count, kind) => {
            if (kind === 'unhandled') {
                // Transport and unexpected failures may never reach backend Sentry.
                Sentry.captureException(new Error('Raw DIPS planning note capture failed'), { extra: { count } })
            } else {
                // Avoid duplicate issues for HTTP failures expected to be reported by the backend.
                console.warn('[dipsSubscriptions] Raw DIPS planning note capture failed', { count })
            }
        },
    })

    const throttledSendPlanningNotes = throttle(
        () => {
            if (!selectHasWriteAccess(useStore.getState())) {
                pendingNotes = []
                return
            }
            if (pendingNotes.length === 0) return
            const notesToSend = pendingNotes
            pendingNotes = []
            void sendPlanningNotes(notesToSend)
        },
        historySyncIntervalMs,
        { leading: true, trailing: true }
    )

    const unsub = useStore.subscribe(
        state => ({
            scheduled: state.dips.entities.scheduledSurgeries.byId,
            unscheduled: state.dips.entities.unScheduledSurgeries.byId,
            hasWriteAccess: selectHasWriteAccess(state),
        }),
        newSurgeries => {
            if (!newSurgeries.hasWriteAccess) {
                pendingNotes = []
            }

            const scheduledArray = Object.values(newSurgeries.scheduled)
            const unscheduledArray = Object.values(newSurgeries.unscheduled)

            capture.observe(convertToRawDipsPlanningNoteObservations(scheduledArray, unscheduledArray))

            if (useAutoSync && newSurgeries.hasWriteAccess) {
                const notes = convertToPlanningNotes(scheduledArray, unscheduledArray)
                if (notes.length > 0) {
                    pendingNotes = notes
                    throttledSendPlanningNotes()
                }
            }
        },
        { equalityFn: shallow }
    )

    return () => {
        unsub()
        throttledSendPlanningNotes.flush()
        capture.stop()
    }
}
